import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

type CloseCheck = { ok: boolean; gated: boolean; archiveDone: boolean; calendarDone: boolean; missingDocs: string[]; checkUnavailable: boolean; owed: boolean; balanceDue: number };
const fmtAmt = (n: number) => '฿' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// "Ready to close this job?" prompt (Calem, bug 3abdfed9 follow-up): once a job is 4. Complete AND
// its client status-update email has actually been sent, offer to move it straight to 6. Closed —
// reusing the exact gate the manual Close button enforces (routes/jobs.ts closeGate, via the new
// read-only /close-check) so the prompt shows what's still missing up front, same wording the
// operator would otherwise only see after clicking Close and being refused.
// Dismissing is session-only (no persisted "seen" flag, by design) — it asks again next time this
// job is opened, same as the operator not having closed it yet.
export default function CloseNowPrompt({ jobId, processLevel, completeEmailSentAt, onClosed }: {
  jobId: string; processLevel: string; completeEmailSentAt: number; onClosed: () => void;
}) {
  const { t } = useTranslation();
  const due = processLevel === '4. Complete' && completeEmailSentAt > 0;
  const [dismissed, setDismissed] = useState(false);
  const [check, setCheck] = useState<CloseCheck | null>(null);
  const [closing, setClosing] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { setDismissed(false); setCheck(null); setErr(''); }, [jobId]);
  useEffect(() => {
    if (!due || dismissed) return;
    let alive = true;
    api.jobCloseCheck(jobId).then((r: any) => { if (alive) setCheck(r); }).catch(() => { /* stay silent — the manual Close button still works */ });
    return () => { alive = false; };
  }, [jobId, due, dismissed]);

  if (!due || dismissed || !check) return null;
  // Only a gated job has a closing checklist; an ungated one can now fail the check on money alone.
  const checklistOpen = check.gated && (!check.archiveDone || !check.calendarDone || check.missingDocs.length > 0);

  const doClose = async () => {
    if (closing) return;
    setClosing(true); setErr('');
    try { await api.updateJob(jobId, { processLevel: '6. Closed' }); setDismissed(true); onClosed(); }
    catch (e: any) { setErr(e?.message || 'Failed'); }
    finally { setClosing(false); }
  };

  const btn: React.CSSProperties = { padding: '7px 16px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
  const ghost: React.CSSProperties = { padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}
      onMouseDown={e => { if (e.target === e.currentTarget && !closing) setDismissed(true); }}>
      <div style={{ width: 440, maxWidth: '92vw', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <i className="ti ti-circle-check" style={{ fontSize: 18, color: check.ok ? '#15803D' : '#B45309' }} aria-hidden />
          <div style={{ fontSize: 15, fontWeight: 800 }}>{t('jb_close_prompt_title')}</div>
        </div>
        {check.ok ? (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{t('jb_close_prompt_ready')}</div>
        ) : (
          <>
            {/* Money still owed blocks Closed on every job (Calem, report 6585fc5a) — no Close button. */}
            {check.owed && <div style={{ fontSize: 13, fontWeight: 700, color: '#DC2626', lineHeight: 1.5, marginBottom: check.checkUnavailable || checklistOpen ? 8 : 0 }}>{t('jb_close_owed', { amt: fmtAmt(check.balanceDue) })}</div>}
            {check.checkUnavailable ? (
              <div style={{ fontSize: 13, color: '#92400E', lineHeight: 1.5 }}>{t('jb_close_prompt_unavailable')}</div>
            ) : checklistOpen && (
              <>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{t('jb_close_prompt_missing_intro')}</div>
                <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 12.5, color: '#92400E' }}>
                  {!check.archiveDone && <li>{t('jb_close_prompt_missing_files')}</li>}
                  {!check.calendarDone && <li>{t('jb_close_prompt_missing_calendar')}</li>}
                  {check.missingDocs.length > 0 && <li>{t('jb_close_prompt_missing_docs_prefix')}{check.missingDocs.join('; ')}</li>}
                </ul>
              </>
            )}
          </>
        )}
        {err && <div style={{ fontSize: 12, color: '#E24B4A', marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button onClick={() => !closing && setDismissed(true)} style={ghost}>{check.ok ? t('jb_close_prompt_not_now') : t('jb_close_prompt_got_it')}</button>
          {check.ok && <button onClick={doClose} disabled={closing} style={{ ...btn, opacity: closing ? 0.6 : 1 }}>{closing ? '…' : t('jb_close_prompt_close_btn')}</button>}
        </div>
      </div>
    </div>
  );
}
